import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PFIFullLogo } from '../common/PFILogo';
import { 
  HomeIcon, 
  CalendarIcon, 
  UsersIcon, 
  BuildingOfficeIcon,
  FolderIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  CogIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

export const Navigation = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (!user) return null;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const adminNavItems = [
    { to: '/dashboard', label: 'Dashboard', icon: HomeIcon },
    { to: '/ai-dashboard', label: 'AI Intelligence', icon: SparklesIcon },
    { to: '/calendar', label: 'Calendar', icon: CalendarIcon },
    { to: '/projects', label: 'Projects', icon: FolderIcon },
    { to: '/customers', label: 'Customers', icon: UsersIcon },
    { to: '/suppliers', label: 'Suppliers', icon: BuildingOfficeIcon },
    { to: '/communications', label: 'Communications', icon: ChatBubbleLeftRightIcon },
    { to: '/queries', label: 'Supplier Queries', icon: ChatBubbleLeftRightIcon },
    { to: '/notifications', label: 'Notifications', icon: BellIcon },
    { to: '/settings', label: 'Settings', icon: CogIcon },
  ];

  const customerNavItems = [
    { to: '/dashboard', label: 'My Projects', icon: HomeIcon },
    { to: '/projects', label: 'Project Details', icon: FolderIcon },
  ];

  const supplierNavItems = [
    { to: '/dashboard', label: 'My Projects', icon: HomeIcon },
    { to: '/projects', label: 'Project Details', icon: FolderIcon },
  ];

  const getNavItems = () => {
    switch (user.role) {
      case 'admin':
        return adminNavItems;
      case 'customer':
        return customerNavItems;
      case 'supplier':
        return supplierNavItems;
      default:
        return [];
    }
  };

  return (
    <nav className="bg-gradient-to-b from-pink-50 to-white shadow-sm border-r border-pink-100 w-64 h-full fixed left-0 top-0 overflow-y-auto">
      <div className="p-6 bg-gradient-to-r from-pink-500 to-orange-400 text-white">
        <Link to="/dashboard" className="flex items-center group">
          <PFIFullLogo size="sm" className="mr-3 filter drop-shadow-sm" />
          <div className="text-xl font-bold hover:scale-105 transition-transform">PFI Connect</div>
        </Link>
        <p className="text-pink-100 text-sm mt-2 flex items-center">
          Pink Flamingo Interiors
        </p>
        <div className="mt-1 text-xs text-pink-100 italic opacity-90">
          "Fun and Fabulous without the Fuss"
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
          Navigation
        </div>
        <ul className="space-y-2">
          {getNavItems().map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md group ${
                    isActive 
                      ? 'bg-pink-50 text-pink-600'
                      : 'text-gray-700 hover:bg-pink-50 hover:text-pink-600'
                  }`}
                >
                  <item.icon className={`mr-3 h-5 w-5 ${
                    isActive ? 'text-pink-500' : 'text-gray-400 group-hover:text-pink-500'
                  }`} />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-200">
        <div className="flex items-center mb-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
            <p className="text-xs text-gray-500 capitalize">{user.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 group"
        >
          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
          Sign Out
        </button>
      </div>
    </nav>
  );
};